import { logger } from "@platform/browser-logger";
import { AxiosInstance } from "axios";
import { RfpData, listBenefitPlanGroups } from "@platform/plan-grid-core";

import { CoverageExportData } from "../types";

/**
 * Fetch benefit plan groups for coverages, optionally filtered by coverage type.
 * When coverageTypes is omitted or empty, all coverages are fetched.
 *
 * Always reads from the server rather than the React Query cache: a cached
 * copy can lag what the Build tab shows, and the export must match it.
 */
export const fetchCoveragesData = async (
  rfp: RfpData,
  ecoRfpId: string,
  axiosInstance: AxiosInstance,
  coverageTypes?: string[],
  coveragePlanTypes?: Record<string, string[]>,
): Promise<CoverageExportData[]> => {
  if (!rfp.coverages || rfp.coverages.length === 0) {
    throw new Error("No coverages found in RFP");
  }

  const hasCoverageFilter =
    coverageTypes !== undefined && coverageTypes.length > 0;
  const coveragesToExport = hasCoverageFilter
    ? rfp.coverages.filter(
        (c) =>
          coverageTypes.includes(c.type) ||
          (c.coverageType && coverageTypes.includes(c.coverageType)),
      )
    : rfp.coverages;

  if (coveragesToExport.length === 0) {
    throw new Error("No coverages match the provided filter");
  }

  logger.info(
    `[btx export] Found ${coveragesToExport.length} coverages to export`,
  );

  const coverageDataPromises = coveragesToExport.map(async (coverage) => {
    const selectedPlanTypes =
      coveragePlanTypes?.[coverage.type] ??
      coveragePlanTypes?.[coverage.coverageType] ??
      [];

    if (
      selectedPlanTypes.length > 0 &&
      coverage.productType &&
      !selectedPlanTypes.includes(coverage.productType)
    ) {
      return null;
    }

    const params = {
      rfpId: ecoRfpId,
      coverageType: coverage.coverageType,
      productType: coverage.productType,
    };

    try {
      const benefitPlanGroups = await listBenefitPlanGroups(
        params,
        axiosInstance,
      );

      const filteredBenefitPlanGroups =
        selectedPlanTypes.length > 0
          ? benefitPlanGroups.filter((group) =>
              selectedPlanTypes.includes(group.productType ?? ""),
            )
          : benefitPlanGroups;

      logger.info(
        `[btx export] Fetched ${filteredBenefitPlanGroups.length} benefit plan groups for ${coverage.type}`,
      );

      return {
        coverage,
        benefitPlanGroups: filteredBenefitPlanGroups,
      };
    } catch (coverageError) {
      logger.error(
        `[btx export] Error fetching data for coverage ${coverage.type}:`,
        coverageError as Error,
      );
      return null;
    }
  });

  const results = await Promise.all(coverageDataPromises);
  const validResults = results.filter(
    (r): r is CoverageExportData => r !== null,
  );

  if (validResults.length === 0) {
    throw new Error("Failed to fetch data for any coverages");
  }

  logger.info(
    `[btx export] Successfully fetched data for ${validResults.length} coverages`,
  );

  return validResults;
};
